import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { toast } from 'sonner';
import type { TumUtilityVendor } from '@/types/tum';

export const useTumUtilityVendors = () => {
  const workspace = useWorkspace();
  const currentWorkspace = workspace?.currentWorkspace;
  const queryClient = useQueryClient();

  const { data: vendors, isLoading, error, refetch } = useQuery({
    queryKey: ['tum-utility-vendors', currentWorkspace?.id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tum_utility_vendors')
        .select('*')
        .eq('workspace_id', currentWorkspace!.id)
        .order('vendor_name', { ascending: true });
      
      if (error) throw error;
      return data as TumUtilityVendor[];
    },
    enabled: !!currentWorkspace?.id,
  });

  const createMutation = useMutation({
    mutationFn: async (vendor: any) => {
      const { data, error } = await supabase
        .from('tum_utility_vendors')
        .insert({ ...vendor, workspace_id: currentWorkspace!.id })
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-utility-vendors'] });
      toast.success('Vendor created successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to create vendor: ${error.message}`);
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, ...updates }: Partial<TumUtilityVendor> & { id: string }) => {
      const { data, error } = await supabase
        .from('tum_utility_vendors')
        .update(updates)
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id)
        .select()
        .single();
      
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-utility-vendors'] });
      toast.success('Vendor updated successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to update vendor: ${error.message}`);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('tum_utility_vendors')
        .delete()
        .eq('id', id)
        .eq('workspace_id', currentWorkspace!.id);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tum-utility-vendors'] });
      toast.success('Vendor deleted successfully');
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete vendor: ${error.message}`);
    },
  });

  return {
    vendors: vendors || [],
    isLoading,
    error,
    refetch,
    createVendor: createMutation.mutate,
    updateVendor: updateMutation.mutate,
    deleteVendor: deleteMutation.mutate,
    isCreating: createMutation.isPending,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
